// Respondent-facing text: answer formatting, piping ({{Q1}} placeholders)
// and translations into the survey's extra languages.
import { coreT, type Locale } from './i18n';
import type { Answers, AnswerValue, Definition, Question, QuestionTranslation, Translation } from './types';
import { escapeRich } from './rich';
import { otherKey } from './types';

/** A readable form of one answer, e.g. option labels instead of ids. */
export function formatAnswer(q: Question, value: AnswerValue | undefined, answers: Answers = {}, locale: Locale = 'en'): string {
  if (value === null || value === undefined) return '';
  const label = (id: unknown) => {
    const o = q.options?.find((x) => x.id === id);
    if (!o) return String(id);
    const other = o.other ? answers[otherKey(q.id)] : undefined;
    return typeof other === 'string' && other.trim() ? other.trim() : o.label;
  };
  switch (q.type) {
    case 'single_choice':
    case 'dropdown':
      return label(value);
    case 'multiple_choice': {
      if (!Array.isArray(value)) return '';
      const rank = (id: unknown) => q.options?.findIndex((o) => o.id === id) ?? -1;
      return [...value].sort((a, b) => rank(a) - rank(b)).map(label).join(', ');
    }
    case 'ranking':
      return Array.isArray(value) ? value.map(label).join(' > ') : '';
    case 'yes_no':
      return typeof value === 'boolean' ? coreT(locale, value ? 'yes' : 'no') : '';
    case 'signature':
      return value ? coreT(locale, 'signed') : '';
  }
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return coreT(locale, value ? 'yes' : 'no');
  if (Array.isArray(value)) return value.join(', ');
  if (q.type === 'file_upload') return typeof value.name === 'string' ? value.name : '';
  if (q.type === 'location') return typeof value.lat === 'number' && typeof value.lng === 'number' ? `${value.lat.toFixed(6)}, ${value.lng.toFixed(6)}` : '';
  const rowLabel = (id: string) => (q.type === 'matrix' ? q.rows : q.options)?.find((o) => o.id === id)?.label ?? id;
  return Object.entries(value)
    .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
    .map(([k, v]) => `${rowLabel(k)}: ${q.type === 'matrix' ? (Array.isArray(v) ? v.map(label).join(', ') : label(v)) : String(v)}`)
    .join('; ');
}

export interface PipeContext {
  /** Resolved questions (loop copies included) to look answers up in. */
  questions: Question[];
  answers: Answers;
  embedded?: Record<string, string>;
  /** Label of the current loop pass, for {{loop}}. */
  loop?: Question['loop'];
  score?: number | null;
  locale?: Locale;
}

const TOKEN = /\{\{\s*([A-Za-z0-9_.:-]{1,64})\s*\}\}/g;

/**
 * Replaces {{ref}} placeholders: a question code or id (its answer), "loop"
 * (the current loop option), "score", or a hidden-field name. Unknown
 * references become empty. Inside a loop, a question of the same loop
 * resolves to this pass's copy.
 */
export function pipe(text: string, ctx: PipeContext, escape: (s: string) => string = (s) => s): string {
  if (!text || !text.includes('{{')) return text;
  const value = (ref: string): string => {
    if (ref === 'loop') return ctx.loop?.label ?? '';
    if (ref === 'score') return ctx.score === null || ctx.score === undefined ? '' : String(ctx.score);
    const lower = ref.toLowerCase();
    const matches = ctx.questions.filter((q) => q.id === ref || q.loop?.baseId === ref || (q.questionCode && q.questionCode.toLowerCase() === lower));
    if (matches.length) {
      const q = (ctx.loop && matches.find((m) => m.loop?.optionId === ctx.loop!.optionId)) || matches.find((m) => !m.loop) || matches[0]!;
      return formatAnswer(q, ctx.answers[q.id], ctx.answers, ctx.locale);
    }
    return ctx.embedded?.[ref] ?? '';
  };
  // `escape` keeps a piped answer from being read as formatting when the text is rich.
  return text.replace(TOKEN, (_, ref: string) => escape(value(ref)));
}

/** A question with its text piped, for display. */
export function pipeQuestion(q: Question, ctx: PipeContext): Question {
  const c = { ...ctx, loop: q.loop ?? ctx.loop };
  const p = (t: string | undefined) => (t === undefined ? t : pipe(t, c));
  const rich = (t: string | undefined) => (t === undefined ? t : pipe(t, c, escapeRich));
  return {
    ...q,
    title: pipe(q.title, c),
    titleRich: rich(q.titleRich),
    description: p(q.description),
    descriptionRich: rich(q.descriptionRich),
    tooltip: p(q.tooltip),
    options: q.options?.map((o) => (o.label.includes('{{') ? { ...o, label: pipe(o.label, c) } : o)),
    rows: q.rows?.map((o) => (o.label.includes('{{') ? { ...o, label: pipe(o.label, c) } : o)),
  };
}

/** Base language first, then the translated ones. */
export function surveyLanguages(def: Definition): string[] {
  const base = def.language || 'en';
  return [base, ...Object.keys(def.translations ?? {}).filter((l) => l !== base)];
}

/** Picks the language to show: the preferred one if offered, else the base. */
export function pickLanguage(def: Definition, preferred: (string | undefined | null)[]): string {
  const langs = surveyLanguages(def);
  for (const p of preferred) {
    if (!p) continue;
    const tag = p.toLowerCase();
    const hit = langs.find((l) => l.toLowerCase() === tag) ?? langs.find((l) => tag.startsWith(l.toLowerCase().split('-')[0]!));
    if (hit) return hit;
  }
  return langs[0]!;
}

function translateQuestion(q: Question, tr: QuestionTranslation | undefined, sections: Record<string, string> | undefined): Question {
  const section = q.sectionTitle?.trim();
  const out: Question = { ...q };
  if (section && sections?.[section]) out.sectionTitle = sections[section];
  if (!tr) return out;
  // A translation is plain text, so the formatted original no longer applies.
  if (tr.title) {
    out.title = tr.title;
    out.titleRich = undefined;
  }
  if (tr.description) {
    out.description = tr.description;
    out.descriptionRich = undefined;
  }
  if (tr.tooltip) out.tooltip = tr.tooltip;
  if (tr.placeholder) out.placeholder = tr.placeholder;
  if (q.scale && (tr.minLabel || tr.maxLabel)) out.scale = { ...q.scale, minLabel: tr.minLabel || q.scale.minLabel, maxLabel: tr.maxLabel || q.scale.maxLabel };
  else if (!q.scale && q.type === 'nps' && (tr.minLabel || tr.maxLabel)) out.scale = { min: 0, max: 10, minLabel: tr.minLabel, maxLabel: tr.maxLabel };
  if (tr.options) out.options = q.options?.map((o) => (tr.options![o.id] ? { ...o, label: tr.options![o.id]! } : o));
  if (tr.rows) out.rows = q.rows?.map((o) => (tr.rows![o.id] ? { ...o, label: tr.rows![o.id]! } : o));
  return out;
}

/**
 * The definition in another language. Missing text falls back to the base
 * language. Section titles are translated consistently, and the per-section
 * settings follow them, so paging and loops behave the same in every language.
 */
export function localize(def: Definition, lang: string | undefined): Definition {
  const tr: Translation | undefined = lang && lang !== (def.language || 'en') ? def.translations?.[lang] : undefined;
  if (!tr) return def;
  const sections = tr.sections;
  let sectionOptions = def.settings.sectionOptions;
  if (sectionOptions && sections) {
    sectionOptions = Object.fromEntries(Object.entries(sectionOptions).map(([k, v]) => [sections[k] || k, v]));
  }
  return {
    ...def,
    welcomeMessage: tr.welcomeMessage || def.welcomeMessage,
    thankYouMessage: tr.thankYouMessage || def.thankYouMessage,
    questions: def.questions.map((q) => translateQuestion(q, tr.questions?.[q.id], sections)),
    settings: { ...def.settings, sectionOptions },
    endings: def.endings?.map((e) => (tr.endings?.[e.id] ? { ...e, message: tr.endings[e.id]! } : e)),
    quotas: def.quotas?.map((q) => (tr.quotas?.[q.id] ? { ...q, message: tr.quotas[q.id]! } : q)),
  };
}

/** Survey title and description in a language (they live outside the definition). */
export function localizeSurveyText(def: Definition, lang: string | undefined, title: string, description: string): { title: string; description: string } {
  const tr = lang && lang !== (def.language || 'en') ? def.translations?.[lang] : undefined;
  return { title: tr?.title || title, description: tr?.description || description };
}
